import {omitBy, isNil, transform} from 'lodash'
import {
  IBIConfig,
  IInputBIConfig,
  NormalizedEventHandlerDictionary,
  Dictionary,
  GenericEventHandler, IBIEventObj, EventCreator
} from 'powtoon-commons/biTracker/types'

export function processConfigHandlers(biconfig: IInputBIConfig, eventCreator = genericEventCreator): IBIConfig {
  return {
    ...biconfig,

    actionEvents: transform(biconfig.actionEvents, (acc, handlers, actionType) => {
      acc[actionType] = normalizeHandlers({handlers, eventCreator})
    }, {} as NormalizedEventHandlerDictionary),

    domEvents: transform(biconfig.domEvents, (acc, handlersObj, eventType) => {
      acc[eventType] = transform(handlersObj, (newHandlersObj, handlers, biTarget) => {
        newHandlersObj[biTarget] = normalizeHandlers({handlers, eventCreator})
      }, {} as NormalizedEventHandlerDictionary)
    }, {} as Dictionary<NormalizedEventHandlerDictionary>)
  }
}

function normalizeHandlers(
  {handlers, eventCreator}: {
    handlers: GenericEventHandler | GenericEventHandler[],
    eventCreator: EventCreator
  }
) {

  if (!Array.isArray(handlers)) {
    handlers = [handlers]
  }

  return handlers.map(handler => {
    switch (typeof handler) {
      case 'string': {
        const parts = handler.split(':')
        if (parts.length !== 3) {
          // no three sections to string - using all as label
          return createSimpleHandler(eventCreator, {label: handler})
        } else {
          // splitting string into category:label:action
          const [category, label, action] = parts
          return createSimpleHandler(eventCreator, {category, label, action})
        }
      }
      case 'object':
        return createSimpleHandler(eventCreator, handler)
      default:
        return handler
    }
  })
}

const createSimpleHandler = (eventCreator: EventCreator, eventParams: Partial<IBIEventObj>) =>
  () => eventCreator(eventParams)

export const eventCreatorFactory = (
  {
    ga4EventName: defaultGa4EventName = null,
    category: defaultCategory = null,
    action: defaultAction = null,
    label: defaultLabel = null,
    value: defaultValue = null,
    autoData: defaultAutoData = null,
    extraData: defaultExtraData = null,
    reportingOptions: defaultreportingOptions = null,
    rudderStackReportingOptions: defaultRudderStackReportingOptions = null,
    segmentReportingOptions: defaultSegmentReportingOptions = null,
    appcuesReportingOptions: defaultAppcuesReportingOptions = null,
    heapReportingOptions: defaultHeapReportingOptions = null,
    dataLayerReportingOptions: defaultDataLayerReportingOptions = null
  }: Partial<IBIEventObj> = {}
) =>
  ({
    ga4EventName = defaultGa4EventName,
    category = defaultCategory,
    action = defaultAction,
    label = defaultLabel,
    value = defaultValue,
    extraData = defaultExtraData,
    autoData = defaultAutoData,
    reportingOptions = defaultreportingOptions,
    rudderStackReportingOptions = defaultRudderStackReportingOptions,
    segmentReportingOptions = defaultSegmentReportingOptions,
    appcuesReportingOptions = defaultAppcuesReportingOptions,
    heapReportingOptions = defaultHeapReportingOptions,
    dataLayerReportingOptions = defaultDataLayerReportingOptions
  }: Partial<IBIEventObj> = {}): IBIEventObj => omitBy({
    ga4EventName,
    category,
    action,
    label,
    value,
    autoData,
    extraData,
    reportingOptions,
    rudderStackReportingOptions,
    segmentReportingOptions,
    appcuesReportingOptions,
    heapReportingOptions,
    dataLayerReportingOptions
  }, isNil) as IBIEventObj

export const genericEventCreator = eventCreatorFactory()
